"use client";

import type { FC } from "react";
import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "motion/react";

const YEAR_PATTERN = /^\d{4}$/;

interface ResultAnimatedHeroValueProps {
  value: string;
  className?: string;
}

/** Roll 4-digit year values toward target (UI9 D18). */
export const ResultAnimatedHeroValue: FC<ResultAnimatedHeroValueProps> = ({
  value,
  className,
}) => {
  const reduceMotion = useReducedMotion();
  const isYear = YEAR_PATTERN.test(value);
  const targetYear = isYear ? Number(value) : null;
  const [displayYear, setDisplayYear] = useState(
    targetYear != null ? targetYear - 5 : null,
  );

  useEffect(() => {
    if (targetYear == null || reduceMotion) {
      return;
    }
    setDisplayYear(targetYear - 5);
    const start = performance.now();
    const duration = 900;
    let frame = 0;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const next = Math.round(
        targetYear - 5 + (targetYear - (targetYear - 5)) * progress,
      );
      setDisplayYear(next);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [reduceMotion, targetYear]);

  if (!isYear || reduceMotion) {
    return <span className={className}>{value}</span>;
  }

  return (
    <motion.span
      className={className}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      data-testid="result-animated-year"
    >
      {displayYear}
    </motion.span>
  );
};
